import { useEffect } from 'react';
import { type DoubleClickTriggerEvent } from '@/components/layouts/gallery-layout';
import { useGallerySelection } from '@/context/GallerySelectionContext';
import { KeyboardKey } from '@/lib/keyboard';
import { type Asset } from '@/types/asset';

/**
 * Enter opens the last-selected asset like a double-click would (DM-1050). Bound globally via
 * `window`, not per-row: a per-row `onKeyDown` only fires while that specific row is mounted and
 * focused, so it went silent the moment a selection carried across a page change and the selected
 * row was no longer rendered — pressing Enter did nothing even though something was still
 * selected. A global listener works regardless of which row (if any) happens to be focused or
 * rendered, resolving its target purely from GallerySelectionContext.
 *
 * `lastSelectedAsset` names the asset a click/toggle most recently acted on and is preferred over
 * `selectedAssets`' own order, which does not reliably track it (a shift-click range keeps
 * already-selected members at their existing array position and only appends newly-covered ones).
 * It also covers select-all, which represents "everything" via the isAllSelected flag and clears
 * `selectedAssets` outright rather than populating it — the array has nothing to fall back to
 * there, so `lastSelectedAsset` is the only source that still resolves to the right asset.
 */
export const useEnterToOpenAsset = (
  handleDoubleClick: ((asset: Asset, event: DoubleClickTriggerEvent) => void) | undefined,
) => {
  const { lastSelectedAsset, selectedAssets } = useGallerySelection();

  useEffect(() => {
    if (!handleDoubleClick) {
      return;
    }

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== KeyboardKey.Enter) {
        return;
      }

      // Existence, not ancestry: this asks "is any dialog/popover open on the page at all",
      // not "is Enter's target inside one". Radix's Dialog/AlertDialog/Popover content always
      // carries role="dialog"/"alertdialog" regardless of the `modal` prop, but `modal={false}`
      // (used deliberately in a couple of places, e.g. the upload dialog, to work around a nested
      // date-time-picker popover) skips Radix's focus trap and aria-hide of the background — so
      // the gallery behind it stays fully interactive, and Enter can dispatch with a target
      // genuinely outside the open dialog's own subtree. Checking existence rather than ancestry
      // sidesteps that: it doesn't matter where the stray keypress landed, only that something
      // dialog-like is open, which is reason enough to leave Enter to it.
      if (document.querySelector('[role="dialog"], [role="alertdialog"]')) {
        return;
      }

      const target = event.target instanceof HTMLElement ? event.target : null;

      // Radix's Checkbox (the row's own select checkbox) calls preventDefault() on every Enter
      // press itself, unconditionally — checkboxes only toggle on Space, so this is Radix
      // declining to act on Enter, not evidence that Enter was meaningfully handled. Trusting
      // defaultPrevented at face value here left Enter doing nothing whenever the checkbox (not
      // the row) held focus. Everywhere else, defaultPrevented is still honored: something else
      // on the page (a menu, some other future control) may have a real reason to claim the
      // keypress.
      const isSelectCheckbox = Boolean(target?.closest('[data-select-column="true"]'));
      if (event.defaultPrevented && !isSelectCheckbox) {
        return;
      }
      if (event.metaKey || event.ctrlKey || event.altKey || event.shiftKey) {
        return;
      }

      // Don't hijack Enter while typing/editing — search, rename, comments, dialogs all rely on it.
      const isTyping = target?.tagName === 'INPUT' || target?.tagName === 'TEXTAREA' || target?.isContentEditable;

      if (isTyping) {
        return;
      }

      const enterTarget = lastSelectedAsset ?? selectedAssets[selectedAssets.length - 1];

      if (!enterTarget) {
        return;
      }

      event.preventDefault();
      handleDoubleClick(enterTarget, event);
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [handleDoubleClick, lastSelectedAsset, selectedAssets]);
};
